import React from "react";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "./config.ts";

import L from "leaflet";
import TravelTimeMap from "./components/TravelTimeMap.tsx";
import Location from "./components/Location";
import { Journey, Route } from "./api/types.ts";
import { queryJourneys, queryRoutes } from "./api/journeyApi.ts";
import RouteCard from "./components/RouteCard.tsx";
import { SectionRef } from "./components/RouteLayer.tsx";
import BottomSheet from "./components/BottomSheet.tsx";
import ControlPanel from "./components/ControlPanel.tsx";
import SettingsPanel from "./components/SettingsPanel.tsx";
import Legend from "./components/Legend.tsx";
import makeDiscs from "./components/Circles.tsx";
import HeatCanvas from "./components/HeatCanvas.tsx";
import { HealthChecker } from "./components/HealthChecker.tsx";
delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LOCATIONS_SEARCH_PARAM = "locations";

const getLocsFromUrl = (searchParams: URLSearchParams): Location[] => {
  return searchParams
    .getAll(LOCATIONS_SEARCH_PARAM)
    .map((locString) => Location.fromString(locString));
};

const currentTimeHHMM = (): string => {
  const now = new Date();
  return (
    String(now.getHours()).padStart(2, "0") +
    ":" +
    String(now.getMinutes()).padStart(2, "0")
  );
};

function App() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState<Location[]>(
    getLocsFromUrl(searchParams),
  );
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [journeysLoading, setJourneysLoading] = useState(false);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [routeDest, setRouteDest] = useState<Location | null>(null);
  const [highlightedSection, setHighlightedSection] =
    useState<SectionRef | null>(null);

  const [time, setTime] = useState(currentTimeHHMM());
  const [maxMinutes, setMaxMinutes] = useState(config.defaultMaxMinutes);
  const [changeView, setChangeView] = useState(true);
  const [backendAwake, setBackendAwake] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(true);

  const discs = useMemo(
    () => makeDiscs(journeys, maxMinutes),
    [journeys, maxMinutes],
  );

  // Memoised so the element identity only changes when the discs do;
  // otherwise every App re-render would tear the canvas layer down.
  const circleLayer = useMemo(() => <HeatCanvas discs={discs} />, [discs]);

  useEffect(() => {
    setChangeView(true);
  }, [locations, backendAwake]);

  useEffect(() => {
    const abortController = new AbortController();

    const refreshJourneys = async () => {
      setJourneysLoading(locations.length > 0);
      try {
        const journeys = await queryJourneys(
          locations,
          time,
          abortController.signal,
        );
        setJourneys(journeys);
        setJourneysLoading(false);
      } catch (e) {
        // An aborted request means a newer one is in flight — leave the
        // loading state to that request.
        if (!axios.isCancel(e) && e.name !== "AbortError") {
          setJourneysLoading(false);
        }
      }
    };

    refreshJourneys();
    return () => {
      // Clean up stale requests.
      abortController.abort();
    };
  }, [locations, time]);

  useEffect(() => {
    searchParams.delete(LOCATIONS_SEARCH_PARAM);
    if (locations.length > 0) {
      for (const loc of locations) {
        searchParams.append(LOCATIONS_SEARCH_PARAM, loc.toString());
      }
    }
    setSearchParams(searchParams);
  }, [locations, searchParams, setSearchParams]);

  useEffect(() => {
    const checkScreenSize = () => {
      // Must match tailwind's `md` breakpoint — RouteCard switches layout
      // there too.
      const smallScreenThreshold = 768;
      setIsSmallScreen(window.innerWidth < smallScreenThreshold);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  // Routes are computed for a specific set of starts and departure time, so
  // they go stale as soon as either changes.
  useEffect(() => {
    setRoutes([]);
    setRouteDest(null);
  }, [locations, time]);

  const showRoute = async (dest: Location) => {
    setRouteDest(dest);
    setRoutes([]);
    try {
      const routes = await queryRoutes(locations, time, dest.coords);
      setRoutes(routes);
    } catch (e) {
      console.error(e);
      setRouteDest(null);
    }
  };

  const clearRoute = () => {
    setRoutes([]);
    setRouteDest(null);
    setHighlightedSection(null);
  };

  const addLoc = (location: Location) => {
    setLocations([...locations, location]);
  };

  const deleteLoc = (idx: number) => {
    setLocations(locations.filter((_, i) => i !== idx));
  };

  // On small screens the directions sheet takes over; everything else comes
  // back when it's closed.
  const hideOverlays = isSmallScreen && routeDest !== null;

  return (
    // 100dvh tracks the real visible height on mobile (the URL bar shrinks
    // 100vh layouts); the h-screen class is the fallback where dvh is
    // unsupported.
    <div className="h-screen" style={{ height: "100dvh" }}>
      <HealthChecker setBackendAwake={setBackendAwake} />
      <TravelTimeMap
        addLoc={addLoc}
        changeView={changeView}
        setChangeView={setChangeView}
        locations={locations}
        CircleLayer={circleLayer}
        routes={routes}
        routeDest={routeDest}
        showRoute={showRoute}
        highlightedSection={highlightedSection}
      />

      {routeDest && (
        <RouteCard
          routes={routes}
          locations={locations}
          dest={routeDest}
          time={time}
          highlight={highlightedSection}
          onHighlight={setHighlightedSection}
          onClose={clearRoute}
        />
      )}

      {/* Desktop: card top-left. Mobile: pull-up sheet at the bottom, like
          the directions sheet. */}
      {!hideOverlays &&
        (isSmallScreen ? (
          <BottomSheet expanded={sheetExpanded} setExpanded={setSheetExpanded}>
            <ControlPanel
              locations={locations}
              addLoc={addLoc}
              deleteLoc={deleteLoc}
              time={time}
              setTime={setTime}
              backendAwake={backendAwake}
              journeysLoading={journeysLoading}
              embedded
              maxMinutes={maxMinutes}
              setMaxMinutes={setMaxMinutes}
            />
          </BottomSheet>
        ) : (
          <div className="absolute left-0 top-0 z-10000 max-h-[90vh] w-96 overflow-y-auto p-3">
            <ControlPanel
              locations={locations}
              addLoc={addLoc}
              deleteLoc={deleteLoc}
              time={time}
              setTime={setTime}
              backendAwake={backendAwake}
              journeysLoading={journeysLoading}
            />
          </div>
        ))}

      {/* On mobile the colour scale lives in the bottom sheet's picker.
          Desktop: scale bottom-right; tapping it expands it into the
          settings panel that adjusts it. */}
      {!hideOverlays && (
        <div className="absolute bottom-0 right-0 z-10000 m-3 hidden md:block">
          {settingsOpen ? (
            <SettingsPanel
              maxMinutes={maxMinutes}
              setMaxMinutes={setMaxMinutes}
              onClose={() => setSettingsOpen(false)}
            />
          ) : (
            discs.length > 0 && (
              <Legend
                maxMinutes={maxMinutes}
                onClick={() => setSettingsOpen(true)}
              />
            )
          )}
        </div>
      )}

    </div>
  );
}

export default App;
